import React, { useMemo, useState, useRef, useEffect } from 'react';
import {
  View,
  Text,
  TextInput,
  TouchableOpacity,
  StyleSheet,
  KeyboardAvoidingView,
  Platform,
  ScrollView,
  ActivityIndicator,
  TextInput as RNTextInput,
  Image,
  ImageSourcePropType,
} from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { useAppDispatch } from '../store';
import { useSelector } from 'react-redux';
import { RootState } from '../store';
import { signIn, forgotPassword, confirmForgotPassword, signInWithGoogle, signInWithApple, signInWithPasskey } from '../store/actions/auth';
import {
  readSignInHint,
  forgetSignInHint,
  METHOD_LABELS,
  type SignInHint,
  type SignInMethod,
} from '../utilities/deviceSignInHint';
import config from '../config';
import { isPasskeySupported } from '../utilities/passkeys';
import { verifyReferralCode } from '../store/actions/partners';
import { getPendingPartnerInvite, setPendingPartnerInvite } from '../utilities/pendingPartnerInvite';
import { setPendingRegisterPrefill } from '../utilities/pendingRegisterPrefill';

import { useTheme } from '../hooks/useTheme';
import Icon from '../components/Icon';
import BrandLogo from '../components/BrandLogo';
import LearnMoreLink from '../components/LearnMoreLink';

const logoImage = require('../../assets/horizontal_logo.png') as ImageSourcePropType;

interface Props {
  navigation: any;
  route?: { params?: Record<string, unknown> };
}

type Screen = 'login' | 'forgot' | 'resetCode';

interface PartnerInvitePreview {
  partner?: {
    isPartnerReferral?: boolean;
    branding?: {
      company?: string;
      brand_logo?: string | null;
    };
  };
}

export default function LoginScreen({ navigation, route }: Props) {
  const dispatch = useAppDispatch();
  const theme = useTheme();
  const styles = useMemo(() => makeStyles(theme), [theme]);
  const rememberMe = useSelector((state: RootState) => (state.session as any).remember_me ?? true);
  const lastLoggedIn = useSelector((state: RootState) => state.session.last_logged_in);

  const [screen, setScreen] = useState<Screen>('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [resetCode, setResetCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [appleLoading, setAppleLoading] = useState(false);
  const [passkeyLoading, setPasskeyLoading] = useState(false);
  // Native: react-native-passkeys is linked (custom EAS build). Web/Electron: WebAuthn.
  // Both share the app.eleplan.com relying party, so a passkey made on the web
  // signs in here too (ED-2799).
  const showPasskeyButton = useMemo(() => isPasskeySupported(), []);
  const [inviteCode, setInviteCode] = useState('');
  const [invitePreview, setInvitePreview] = useState<PartnerInvitePreview | null>(null);
  const settings = useSelector((state: RootState) => state.session.settings);
  // Mirror web: hide Google button when the system setting is explicitly disabled.
  // If settings haven't loaded yet (pre-login, settings === false), default to showing
  // the button so it's visible on first launch before any session is established.
  const showGoogleButton = !!config.cognito.appClientId && (settings === false || settings?.enable_google_signin === true);
  const showAppleButton = Platform.OS === 'ios' && !!config.cognito.appClientId && (settings === false || settings?.enable_apple_signin === true);

  const passwordRef = useRef<RNTextInput>(null);
  const newPasswordRef = useRef<RNTextInput>(null);
  const prefillAttemptedRef = useRef(false);
  const emailManuallyClearedRef = useRef(false);
  const resetRequestVersion = useRef(0);

  useEffect(() => () => { resetRequestVersion.current += 1; }, []);

  useEffect(() => {
    if (prefillAttemptedRef.current) return;
    if (emailManuallyClearedRef.current) return;
    if (screen !== 'login') return;
    if (email.trim().length > 0) return;

    const routeEmail = typeof route?.params?.email === 'string' ? String(route.params.email).trim() : '';
    if (routeEmail) {
      setEmail(routeEmail);
      prefillAttemptedRef.current = true;
      return;
    }

    if (rememberMe && typeof lastLoggedIn?.email === 'string' && lastLoggedIn.email.trim()) {
      setEmail(lastLoggedIn.email.trim());
      prefillAttemptedRef.current = true;
      return;
    }
    // Mark attempted so manual clears don't get auto-refilled.
    prefillAttemptedRef.current = true;
  }, [screen, route?.params?.email, rememberMe, lastLoggedIn?.email]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const routeCode = typeof route?.params?.inviteCode === 'string'
        ? String(route.params.inviteCode).trim()
        : '';
      const routeEmail = typeof route?.params?.email === 'string'
        ? String(route.params.email).trim().toLowerCase()
        : '';
      const stored = await getPendingPartnerInvite();
      const storedEmail = stored?.code === routeCode ? stored.email : undefined;
      const pending = routeCode
        ? { code: routeCode, ...((routeEmail || storedEmail) ? { email: routeEmail || storedEmail } : {}) }
        : stored;
      if (cancelled || !pending?.code) return;

      if (routeCode && (routeEmail || stored?.code !== routeCode)) {
        await setPendingPartnerInvite(pending);
      }
      if (cancelled) return;
      setInviteCode(pending.code);
      if (!emailManuallyClearedRef.current && !email.trim() && pending.email) {
        setEmail(pending.email);
      }

      const preview = await verifyReferralCode(pending.code);
      if (!cancelled && preview?.valid && preview?.partner?.isPartnerReferral) {
        setInvitePreview(preview as PartnerInvitePreview);
      }
    })();
    return () => { cancelled = true; };
  }, [route?.params?.inviteCode, route?.params?.email]);

  // Register-by-chat signup handoff (?signup_handoff=) is handled at the App
  // level now (App.tsx) so it also works when another account is already signed
  // in — it can't live here because LoginScreen only renders when logged out.

  const handlePasskeyLogin = async () => {
    if (passkeyLoading || loading) return;
    clearError();
    setPasskeyLoading(true);
    const result = await dispatch(signInWithPasskey(inviteCode || undefined));
    setPasskeyLoading(false);
    // A dismissed OS prompt is not an error worth showing.
    const cancelled = (result as { cancelled?: boolean } | undefined)?.cancelled === true;
    if (!result?.success && !cancelled) {
      setError(result?.message || 'Passkey sign-in failed.');
    }
  };

  // What this device signed in with last time. Read once; absent on a fresh
  // install, after "Not you?", and in a private window — all of which simply
  // fall back to the old behaviour.
  const [hint, setHint] = useState<SignInHint | null>(null);
  // Set when someone picks a provider that differs from the hint. Holds the
  // action so confirming can run it unchanged.
  const [divergence, setDivergence] = useState<{ method: SignInMethod; run: () => void } | null>(null);

  useEffect(() => {
    let cancelled = false;
    readSignInHint().then((value) => { if (!cancelled) setHint(value); }).catch(() => {});
    return () => { cancelled = true; };
  }, []);

  const dismissHint = async () => {
    setHint(null);
    setDivergence(null);
    await forgetSignInHint().catch(() => {});
  };

  /**
   * Tapping a different provider than last time is the exact moment a second
   * account gets created, so it is the moment to ask. Confirming proceeds
   * unchanged — this only ever adds a question, never blocks or auto-links,
   * because a shared device says "same phone", not "same person".
   */
  const guardDivergence = (method: SignInMethod, run: () => void) => {
    if (!hint || hint.method === method) { run(); return; }
    setDivergence({ method, run });
  };

  const runGoogleLogin = async () => {
    if (googleLoading) return;
    setGoogleLoading(true);
    await dispatch(signInWithGoogle(undefined, inviteCode || undefined));
    setGoogleLoading(false);
  };

  const runAppleLogin = async () => {
    if (appleLoading) return;
    setAppleLoading(true);
    await dispatch(signInWithApple(undefined, inviteCode || undefined));
    setAppleLoading(false);
  };

  const handleGoogleLogin = () => guardDivergence('google', runGoogleLogin);
  const handleAppleLogin = () => guardDivergence('apple', runAppleLogin);

  const clearError = () => setError('');

  const handleLogin = async () => {
    clearError();
    if (!email.trim()) { setError('Please enter your email.'); return; }
    if (!password) { setError('Please enter your password.'); return; }

    setLoading(true);
    const result = await dispatch(signIn(email, password, inviteCode || undefined)) as any;
    setLoading(false);

    if (result?.challenge === 'MFA') {
      navigation.navigate('MFA', { flow: 'login', email });
    } else if (result?.challenge === 'FORCE_PASSWORD') {
      navigation.navigate('ForcePassword', { email });
    } else if (!result?.success) {
      setError(result?.message || 'Sign in failed. Please try again.');
    }
  };

  const handleRegister = async () => {
    if (inviteCode) {
      const invitedEmail = email.trim() || undefined;
      await Promise.all([
        setPendingPartnerInvite({ code: inviteCode, email: invitedEmail }),
        setPendingRegisterPrefill({ code: inviteCode, email: invitedEmail }),
      ]);
    }
    navigation.navigate('Register');
  };

  const cancelPasswordReset = () => {
    resetRequestVersion.current += 1;
    setLoading(false);
    setScreen('login');
    clearError();
  };

  const handleForgotPassword = () => {
    if (loading) return;
    clearError();
    if (!email.trim()) { setError('Please enter your email to reset your password.'); return; }
    const request = ++resetRequestVersion.current;
    setLoading(true);
    return dispatch(forgotPassword(email)).then((result: any) => {
      if (request !== resetRequestVersion.current) return;
      setLoading(false);
      if (result?.success) {
        setScreen('resetCode');
      } else {
        setError(result?.message || 'Could not send reset code.');
      }
    });
  };

  const handleResetPassword = () => {
    if (loading) return;
    clearError();
    if (!resetCode.trim()) { setError('Please enter the code from your email.'); return; }
    if (!newPassword || newPassword.length < 8) { setError('Password must be at least 8 characters.'); return; }
    const request = ++resetRequestVersion.current;
    setLoading(true);
    return dispatch(confirmForgotPassword(email, resetCode, newPassword)).then((result: any) => {
      if (request !== resetRequestVersion.current) return;
      setLoading(false);
      if (result?.success) {
        setScreen('login');
        setPassword(newPassword);
      } else {
        setError(result?.message || 'Could not reset password.');
      }
    });
  };

  const tagline =
    screen === 'login'
      ? (inviteCode ? 'Sign in to accept your partner invitation' : 'Sign in to access your care plans')
      : screen === 'forgot'
        ? 'Reset your password'
        : 'Enter your reset code';

  return (
    <SafeAreaView style={[styles.flex, { backgroundColor: theme.colors.background }]} edges={['top', 'left', 'right']}>
      <KeyboardAvoidingView
        style={styles.flex}
        behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
        keyboardVerticalOffset={Platform.OS === 'ios' ? 0 : 20}
      >
        <ScrollView
          contentContainerStyle={styles.scroll}
          keyboardShouldPersistTaps="handled"
          showsVerticalScrollIndicator={false}
        >
          {/* Full logo + tagline */}
          <View style={styles.header}>
            <BrandLogo
              fallback={logoImage}
              style={styles.logo}
              resizeMode="contain"
              accessibilityLabel="Eleplan"
              whitelabelOverride={
                invitePreview?.partner?.isPartnerReferral
                  ? {
                      brand_logo: invitePreview.partner.branding?.brand_logo,
                      company_name: invitePreview.partner.branding?.company,
                    }
                  : null
              }
            />
            <Text style={[styles.tagline, { color: theme.colors.muted }]}>{tagline}</Text>
          </View>

        {/* Card */}
        <View style={[styles.card, { backgroundColor: theme.colors.card, borderColor: theme.colors.border }]}>

          {/* Login Form */}
          {screen === 'login' && (
            <>
              {!!inviteCode && (
                <View style={[styles.inviteCard, { borderColor: theme.colors.primary, backgroundColor: theme.colors.surface }]}>
                  <Text style={[styles.inviteTitle, { color: theme.colors.text }]}>Partner invitation ready</Text>
                  <Text style={[styles.inviteCopy, { color: theme.colors.muted }]}>
                    {invitePreview?.partner?.branding?.company
                      ? `Sign in with the invited email to accept ${invitePreview.partner.branding.company}'s invitation automatically.`
                      : 'Sign in with the invited email and we’ll accept the invitation automatically.'}
                  </Text>
                </View>
              )}
              <View style={styles.field}>
                <Text style={[styles.label, { color: theme.colors.muted }]}>Email</Text>
                <TextInput
                  style={[styles.input, { borderColor: theme.colors.border, color: theme.colors.text, backgroundColor: theme.colors.surface }]}
                  value={email}
                  onChangeText={(t) => {
                    if (screen === 'login' && t.trim().length === 0) {
                      emailManuallyClearedRef.current = true;
                      // Prevent route-param rehydration from immediately re-filling.
                      if (typeof navigation?.setParams === 'function') {
                        navigation.setParams({ email: undefined });
                      }
                    } else if (t.trim().length > 0) {
                      emailManuallyClearedRef.current = false;
                    }
                    setEmail(t);
                    clearError();
                  }}
                  placeholder="you@example.com"
                  placeholderTextColor={theme.colors.muted}
                  keyboardType="email-address"
                  autoCapitalize="none"
                  autoCorrect={false}
                  returnKeyType="next"
                  onSubmitEditing={() => passwordRef.current?.focus()}
                  editable={!loading}
                />
              </View>

              <View style={styles.field}>
                <View style={styles.labelRow}>
                  <Text style={[styles.label, { color: theme.colors.muted }]}>Password</Text>
                  <TouchableOpacity
                    onPress={() => { setResetCode(''); setNewPassword(''); clearError(); setScreen('forgot'); }}
                    disabled={loading}
                    accessibilityRole="button"
                    accessibilityLabel="Forgot password?"
                  >
                    <Text style={[styles.link, { color: theme.colors.primary }]}>Forgot password?</Text>
                  </TouchableOpacity>
                </View>
                <View style={styles.passwordWrap}>
                  <TextInput
                    ref={passwordRef}
                    style={[styles.input, styles.passwordInput, { borderColor: theme.colors.border, color: theme.colors.text, backgroundColor: theme.colors.surface }]}
                    value={password}
                    onChangeText={(t) => { setPassword(t); clearError(); }}
                    placeholder="••••••••"
                    placeholderTextColor={theme.colors.muted}
                    secureTextEntry={!showPassword}
                    returnKeyType="go"
                    onSubmitEditing={handleLogin}
                    editable={!loading}
                  />
                  <TouchableOpacity style={styles.eyeBtn} onPress={() => setShowPassword(!showPassword)}>
                    <Text style={{ color: theme.colors.muted, fontSize: 13 }}>{showPassword ? 'Hide' : 'Show'}</Text>
                  </TouchableOpacity>
                </View>
              </View>

              {!!error && <Text style={styles.errorText}>{error}</Text>}

              <TouchableOpacity
                style={[styles.button, { backgroundColor: theme.colors.primary }, loading && styles.buttonDisabled]}
                onPress={handleLogin}
                disabled={loading}
                activeOpacity={0.85}
                accessibilityRole="button"
                accessibilityLabel="Sign In"
              >
                {loading
                  ? <ActivityIndicator color="#fff" size="small" />
                  : <Text style={styles.buttonText}>{inviteCode ? 'Sign In & Accept Invitation' : 'Sign In'}</Text>
                }
              </TouchableOpacity>

              {showPasskeyButton && (
                <>
                  <View style={styles.orDivider}>
                    <View style={[styles.orLine, { backgroundColor: theme.colors.border }]} />
                    <Text style={[styles.orText, { color: theme.colors.muted }]}>or</Text>
                    <View style={[styles.orLine, { backgroundColor: theme.colors.border }]} />
                  </View>
                  <TouchableOpacity
                    style={[styles.biometricBtn, { borderColor: theme.colors.border, backgroundColor: theme.colors.surface }]}
                    onPress={handlePasskeyLogin}
                    disabled={passkeyLoading || loading}
                    activeOpacity={0.85}
                    accessibilityRole="button"
                    accessibilityLabel="Sign in with a passkey"
                  >
                    {passkeyLoading
                      ? <ActivityIndicator color={theme.colors.primary} size="small" />
                      : (
                        <View style={styles.biometricInner}>
                          <Icon icon={['fad', 'fingerprint']} size={18} color={theme.colors.primary} />
                          <Text style={[styles.biometricText, { color: theme.colors.text }]}>Sign in with a passkey</Text>
                        </View>
                      )
                    }
                  </TouchableOpacity>
                </>
              )}

              {hint && !divergence && (
                <View style={[styles.hintCard, { backgroundColor: theme.colors.surface, borderColor: theme.colors.border }]}>
                  <Text style={[styles.hintText, { color: theme.colors.muted }]}>
                    Last time on this device you signed in with {METHOD_LABELS[hint.method]} as{' '}
                    <Text style={{ color: theme.colors.text, fontWeight: '600' }}>{hint.emailMasked}</Text>.
                  </Text>
                  <TouchableOpacity accessibilityRole="button" onPress={dismissHint} hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}>
                    <Text style={[styles.hintDismiss, { color: theme.colors.primary }]}>Not you?</Text>
                  </TouchableOpacity>
                </View>
              )}

              {hint && divergence && (
                <View style={[styles.hintCard, { backgroundColor: theme.colors.surface, borderColor: theme.colors.primary }]}>
                  <Text style={[styles.hintText, { color: theme.colors.text }]}>
                    This device last signed in with {METHOD_LABELS[hint.method]} as{' '}
                    <Text style={{ fontWeight: '600' }}>{hint.emailMasked}</Text>. Continuing with{' '}
                    {METHOD_LABELS[divergence.method]} may start a separate account.
                  </Text>
                  <View style={styles.hintActions}>
                    <TouchableOpacity
                      accessibilityRole="button"
                      onPress={() => {
                        const proceed = divergence.run;
                        setDivergence(null);
                        proceed();
                      }}
                      style={styles.hintSecondary}
                    >
                      <Text style={[styles.hintSecondaryText, { color: theme.colors.muted }]}>Continue anyway</Text>
                    </TouchableOpacity>
                    <TouchableOpacity
                      accessibilityRole="button"
                      onPress={() => setDivergence(null)}
                      style={[styles.hintPrimary, { backgroundColor: theme.colors.primary }]}
                    >
                      <Text style={styles.hintPrimaryText}>Use {METHOD_LABELS[hint.method]}</Text>
                    </TouchableOpacity>
                  </View>
                </View>
              )}

              {showGoogleButton && (
                <>
                  <View style={styles.orDivider}>
                    <View style={[styles.orLine, { backgroundColor: theme.colors.border }]} />
                    <Text style={[styles.orText, { color: theme.colors.muted }]}>or</Text>
                    <View style={[styles.orLine, { backgroundColor: theme.colors.border }]} />
                  </View>
                  <TouchableOpacity
                    style={[styles.googleBtn, { borderColor: theme.colors.border, backgroundColor: theme.colors.surface }]}
                    onPress={handleGoogleLogin}
                    disabled={googleLoading || loading}
                    activeOpacity={0.85}
                  >
                    {googleLoading
                      ? <ActivityIndicator color={theme.colors.primary} size="small" />
                      : (
                        <View style={styles.googleInner}>
                          <Text style={styles.googleG}>G</Text>
                          <Text style={[styles.googleText, { color: theme.colors.text }]}>
                            Sign in with Google
                          </Text>
                        </View>
                      )
                    }
                  </TouchableOpacity>
                </>
              )}

              {showAppleButton && (
                <>
                  <View style={styles.orDivider}>
                    <View style={[styles.orLine, { backgroundColor: theme.colors.border }]} />
                    <Text style={[styles.orText, { color: theme.colors.muted }]}>or</Text>
                    <View style={[styles.orLine, { backgroundColor: theme.colors.border }]} />
                  </View>
                  <TouchableOpacity
                    style={[styles.appleBtn, { borderColor: theme.colors.border }]}
                    onPress={handleAppleLogin}
                    disabled={appleLoading || loading}
                    activeOpacity={0.85}
                  >
                    {appleLoading
                      ? <ActivityIndicator color="#fff" size="small" />
                      : (
                        <View style={styles.appleInner}>
                          <Icon icon={['fab', 'apple']} size={16} color="#fff" />
                          <Text style={styles.appleText}>Sign in with Apple</Text>
                        </View>
                      )
                    }
                  </TouchableOpacity>
                </>
              )}

              {(showGoogleButton || showAppleButton) && (
                <LearnMoreLink
                  slug="google-sign-in"
                  label="About Google & Apple sign-in"
                  align="center"
                />
              )}

              <TouchableOpacity style={styles.backBtn} onPress={handleRegister}>
                <Text style={{ fontSize: 13, color: theme.colors.muted }}>
                  {inviteCode ? 'New to Eleplan? ' : "Don't have an account? "}
                  <Text style={{ color: theme.colors.primary, fontWeight: '600' }}>
                    {inviteCode ? 'Create an account for this invitation' : 'Sign up'}
                  </Text>
                </Text>
              </TouchableOpacity>
            </>
          )}

          {/* Forgot Password */}
          {screen === 'forgot' && (
            <>
              <View style={styles.field}>
                <Text style={[styles.label, { color: theme.colors.muted }]}>Email</Text>
                <TextInput
                  style={[styles.input, { borderColor: theme.colors.border, color: theme.colors.text, backgroundColor: theme.colors.surface }]}
                  value={email}
                  onChangeText={(t) => { setEmail(t); clearError(); }}
                  placeholder="you@example.com"
                  placeholderTextColor={theme.colors.muted}
                  keyboardType="email-address"
                  autoCapitalize="none"
                  autoCorrect={false}
                  returnKeyType="go"
                  onSubmitEditing={handleForgotPassword}
                  editable={!loading}
                />
              </View>

              {!!error && <Text style={styles.errorText}>{error}</Text>}

              <TouchableOpacity
                style={[styles.button, { backgroundColor: theme.colors.primary }, loading && styles.buttonDisabled]}
                onPress={handleForgotPassword}
                disabled={loading}
                activeOpacity={0.85}
                accessibilityRole="button"
                accessibilityLabel="Send Reset Code"
              >
                {loading
                  ? <ActivityIndicator color="#fff" size="small" />
                  : <Text style={styles.buttonText}>Send Reset Code</Text>
                }
              </TouchableOpacity>

              <TouchableOpacity
                style={styles.backBtn}
                onPress={cancelPasswordReset}
                accessibilityRole="button"
                accessibilityLabel="Back to sign in"
              >
                <Text style={[styles.link, { color: theme.colors.muted }]}>← Back to sign in</Text>
              </TouchableOpacity>
            </>
          )}

          {/* Reset Code */}
          {screen === 'resetCode' && (
            <>
              <Text style={[styles.hint, { color: theme.colors.muted }]}>
                A reset code was sent to <Text style={{ fontWeight: '600', color: theme.colors.text }}>{email}</Text>
              </Text>

              <View style={styles.field}>
                <Text style={[styles.label, { color: theme.colors.muted }]}>Reset Code</Text>
                <TextInput
                  style={[styles.input, { borderColor: theme.colors.border, color: theme.colors.text, backgroundColor: theme.colors.surface }]}
                  value={resetCode}
                  onChangeText={(t) => { setResetCode(t); clearError(); }}
                  placeholder="123456"
                  placeholderTextColor={theme.colors.muted}
                  keyboardType="number-pad"
                  returnKeyType="next"
                  onSubmitEditing={() => newPasswordRef.current?.focus()}
                  editable={!loading}
                />
              </View>

              <View style={styles.field}>
                <Text style={[styles.label, { color: theme.colors.muted }]}>New Password</Text>
                <TextInput
                  ref={newPasswordRef}
                  style={[styles.input, { borderColor: theme.colors.border, color: theme.colors.text, backgroundColor: theme.colors.surface }]}
                  value={newPassword}
                  onChangeText={(t) => { setNewPassword(t); clearError(); }}
                  placeholder="Min. 8 characters"
                  placeholderTextColor={theme.colors.muted}
                  secureTextEntry
                  returnKeyType="go"
                  onSubmitEditing={handleResetPassword}
                  editable={!loading}
                />
              </View>

              {!!error && <Text style={styles.errorText}>{error}</Text>}

              <TouchableOpacity
                style={[styles.button, { backgroundColor: theme.colors.primary }, loading && styles.buttonDisabled]}
                onPress={handleResetPassword}
                disabled={loading}
                activeOpacity={0.85}
                accessibilityRole="button"
                accessibilityLabel="Reset Password"
              >
                {loading
                  ? <ActivityIndicator color="#fff" size="small" />
                  : <Text style={styles.buttonText}>Reset Password</Text>
                }
              </TouchableOpacity>

              <TouchableOpacity style={styles.backBtn} onPress={cancelPasswordReset} accessibilityRole="button" accessibilityLabel="Back to sign in">
                <Text style={[styles.link, { color: theme.colors.muted }]}>← Back to sign in</Text>
              </TouchableOpacity>
            </>
          )}
        </View>
        </ScrollView>
      </KeyboardAvoidingView>
    </SafeAreaView>
  );
}

const makeStyles = (theme: ReturnType<typeof import('../hooks/useTheme').useTheme>) =>
  StyleSheet.create({
    flex: { flex: 1 },
    scroll: {
      flexGrow: 1,
      justifyContent: 'center',
      paddingHorizontal: 28,
      paddingTop: 24,
      paddingBottom: 40,
    },
    header: {
      alignItems: 'center',
      marginBottom: 28,
      // Match the card so the logo/tagline column lines up with the form
      // on desktop. On phones the window is narrower than 480 so this is
      // effectively a no-op; on desktop it prevents the header from
      // stretching edge-to-edge while the card sits centered below.
      width: '100%',
      maxWidth: 480,
      alignSelf: 'center',
    },
    logo: {
      width: '100%',
      maxWidth: 320,
      height: 64,
      marginBottom: 10,
    },
    tagline: {
      fontSize: 15,
      fontWeight: '500',
    },
    card: {
      borderRadius: 20,
      borderWidth: StyleSheet.hairlineWidth,
      padding: 26,
      shadowColor: '#000',
      shadowOffset: { width: 0, height: 4 },
      shadowOpacity: 0.08,
      shadowRadius: 12,
      elevation: 3,
      // Cap the auth card at a phone-shaped width on desktop so the form
      // fields don't stretch to ~1000px. `width: 100%` keeps it filling
      // the scroll container's horizontal padding on phones.
      width: '100%',
      maxWidth: 480,
      alignSelf: 'center',
    },
    inviteCard: {
      borderWidth: 1,
      borderRadius: 12,
      padding: 14,
      marginBottom: 18,
    },
    inviteTitle: {
      fontSize: 15,
      fontWeight: '700',
      marginBottom: 4,
    },
    inviteCopy: {
      fontSize: 13,
      lineHeight: 19,
    },
    field: {
      marginBottom: 16,
    },
    labelRow: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: 6,
    },
    label: {
      fontSize: 13,
      fontWeight: '500',
      marginBottom: 6,
    },
    link: {
      fontSize: 13,
      fontWeight: '500',
    },
    input: {
      borderWidth: 1,
      borderRadius: 10,
      paddingHorizontal: 14,
      paddingVertical: 12,
      fontSize: 15,
    },
    passwordWrap: {
      position: 'relative',
    },
    passwordInput: {
      paddingRight: 56,
    },
    eyeBtn: {
      position: 'absolute',
      right: 14,
      top: 0,
      bottom: 0,
      justifyContent: 'center',
    },
    button: {
      height: 50,
      borderRadius: 12,
      alignItems: 'center',
      justifyContent: 'center',
      marginTop: 8,
    },
    buttonDisabled: {
      opacity: 0.7,
    },
    buttonText: {
      color: '#fff',
      fontSize: 15,
      fontWeight: '600',
    },
    errorText: {
      color: theme.colors.error,
      fontSize: 13,
      marginBottom: 10,
      marginTop: -4,
    },
    hint: {
      fontSize: 13,
      lineHeight: 20,
      marginBottom: 16,
    },
    backBtn: {
      alignItems: 'center',
      paddingTop: 16,
    },
    hintCard: {
    borderWidth: StyleSheet.hairlineWidth,
    borderRadius: 10,
    padding: 12,
    marginTop: 14,
    gap: 8,
  },
  hintText: { fontSize: 12.5, lineHeight: 18 },
  hintDismiss: { fontSize: 12.5, fontWeight: '600' },
  hintActions: { flexDirection: 'row', justifyContent: 'flex-end', alignItems: 'center', gap: 8 },
  hintSecondary: { paddingVertical: 8, paddingHorizontal: 10 },
  hintSecondaryText: { fontSize: 12.5, fontWeight: '600' },
  hintPrimary: { paddingVertical: 8, paddingHorizontal: 14, borderRadius: 8 },
  hintPrimaryText: { color: '#fff', fontSize: 12.5, fontWeight: '600' },
  orDivider: {
      flexDirection: 'row',
      alignItems: 'center',
      marginVertical: 16,
      gap: 10,
    },
    orLine: { flex: 1, height: StyleSheet.hairlineWidth },
    orText: { fontSize: 12, fontWeight: '500' },
    biometricBtn: {
      height: 50,
      borderRadius: 12,
      borderWidth: 1,
      alignItems: 'center',
      justifyContent: 'center',
    },
    biometricInner: { flexDirection: 'row', alignItems: 'center', gap: 10 },
    biometricText: { fontSize: 15, fontWeight: '500' },
    googleBtn: {
      height: 50,
      borderRadius: 12,
      borderWidth: 1,
      alignItems: 'center',
      justifyContent: 'center',
    },
    googleInner: { flexDirection: 'row', alignItems: 'center', gap: 10 },
    googleG: { fontSize: 18, fontWeight: '700', color: '#4285F4' },
    googleText: { fontSize: 15, fontWeight: '500' },
    appleBtn: {
      height: 50,
      borderRadius: 12,
      borderWidth: 1,
      backgroundColor: '#000',
      alignItems: 'center',
      justifyContent: 'center',
    },
    appleInner: { flexDirection: 'row', alignItems: 'center', gap: 8 },
    appleText: { color: '#fff', fontSize: 15, fontWeight: '600' },
  });
